import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { PUSH_FRAME_EVENT } from '../wasm/pushConnection';
import { OPEN_CENTER_EVENT } from './notifications/events';
import { CATEGORIES, type Family } from './notifications/categories';

// IbmNotificationToaster — the live glance (design §4). A transient, high-signal
// pointer into the durable notification center, NOT the system itself. Mounted
// once at the app root (gatsby-browser/ssr) so an arrival surfaces on any page.
// It listens to the same content-free PUSH_FRAME_EVENT the center does; clicking
// a toast opens the center (it does NOT deep-link — the WS frame carries no
// ref_id), and the durable badge/center persist the item after the toast fades.
//
// High-signal categories only — action-needed arrivals. Title/body/family AND the
// suppress-on surface come from the shared CATEGORIES map (./notifications/
// categories): a toast fires only for a category with a `toast` descriptor (the
// action categories), and is suppressed on that category's own route (the live
// inbox is the better surface; this also avoids a toast on the user's own action,
// which echoes ibm_changed back to their devices). The informational/terminal
// lifecycle events and -outgoing echoes have no `toast`, so they're center-only.

const MAX_VISIBLE = 3;
const DURATION_MS = 6000;
const CYAN = '#46c8f5';

interface ToastItem { id: number; title: string; body: string; family: Family }

const toastIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const Stack = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Card = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 300px;
  background: #161d25;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
  box-shadow: inset 3px 0 0 ${CYAN}, 0 10px 30px rgba(0, 0, 0, 0.5);
  padding: 11px 12px;
  cursor: pointer;
  animation: ${toastIn} 0.28s ease;
`;
const More = styled.button`
  align-self: flex-end;
  background: none;
  border: 0;
  padding: 4px 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${CYAN};
  cursor: pointer;
`;

const Glyph: React.FC<{ family: Family }> = ({ family }) => {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: CYAN, strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (family === 'message') {
    return <svg {...common}><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 0 1 21 11.5z" /></svg>;
  }
  if (family === 'team') {
    return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>;
  }
  return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>; // booking = calendar
};

export const IbmNotificationToaster: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const openCenter = useCallback(() => {
    window.dispatchEvent(new CustomEvent(OPEN_CENTER_EVENT));
  }, []);
  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    let seq = 0;
    const onFrame = (e: Event) => {
      const detail = (e as CustomEvent).detail as { oobm?: string; category?: string; settled?: boolean } | undefined;
      if (detail?.oobm !== 'ibm_changed' || !detail.category) return;
      // A settle nudge means the item was just ANSWERED — often by this very
      // user, seconds ago. Toasting the ask they just satisfied ("confirm
      // your paper key" right after they proved it) reads as a broken
      // promise. Refresh flows still fire; only the toast is suppressed.
      if (detail.settled) return;
      const d = CATEGORIES[detail.category];
      if (!d?.toast) return; // center-only category — no toast
      if (window.location.pathname.startsWith(d.route)) return; // suppress on the live surface

      const id = ++seq;
      setToasts(prev => [...prev, { id, title: d.toast!.title, body: d.toast!.body, family: d.family }]);
      window.setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), DURATION_MS);
    };
    window.addEventListener(PUSH_FRAME_EVENT, onFrame);
    return () => window.removeEventListener(PUSH_FRAME_EVENT, onFrame);
  }, []);

  if (toasts.length === 0) return null;

  // Newest on top; cap the visible stack, collapse the rest into the center.
  const newestFirst = [...toasts].reverse();
  const shown = newestFirst.slice(0, MAX_VISIBLE);
  const extra = newestFirst.length - shown.length;

  return (
    <Stack data-testid="toast-stack">
      {shown.map(t => (
        <Card
          key={t.id}
          data-testid="toast"
          data-family={t.family}
          role="status"
          onClick={() => { dismiss(t.id); openCenter(); }}
        >
          <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, background: 'rgba(70,200,245,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Glyph family={t.family} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#eef2f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
            <div style={{ fontSize: 11.5, fontWeight: 500, color: '#8893a2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body}</div>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={e => { e.stopPropagation(); dismiss(t.id); }}
            style={{ flexShrink: 0, background: 'none', border: 0, color: '#69737f', fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: 2 }}
          >
            ×
          </button>
        </Card>
      ))}
      {extra > 0 && (
        <More type="button" data-testid="toast-more" onClick={openCenter}>
          +{extra} more in your center
        </More>
      )}
    </Stack>
  );
};

export default IbmNotificationToaster;
